import { useDashboardBridgeOptional } from '@/components/organisms/Dashboard/useDashboardBridge'
import type { ChatToolCall } from '@/services/api/domains/chatbot.api'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

type QueryFeature = {
  geometry?: GeoJSON.Geometry
  properties?: Record<string, unknown>
}

type QueryResult = {
  features?: QueryFeature[]
}

/**
 * Renders an assistant chat message as markdown. When the assistant's message
 * is accompanied by query_features tool calls that returned geometry, body rows
 * in markdown tables become clickable — click emits `zoom-to-feature` so the
 * sibling map element pans to that feature.
 *
 * Row-to-feature matching mirrors the legacy webapp:
 * a closure counter increments per body <tr> render (remark-gfm emits one tr
 * per data row in DOM order), and features are looked up by index across all
 * query_features tool calls in the message.
 */
export function ChatbotMessage({ content, toolCalls }: { content: string; toolCalls?: ChatToolCall[] }) {
  // Null on the sitewide assistant surface — there is no DashboardBridgeProvider
  // there (Task 8). Rows fall through to the non-clickable branch below rather
  // than attach a handler that would call a method on null.
  const bridge = useDashboardBridgeOptional()

  const features: QueryFeature[] = []

  for (const tc of toolCalls ?? []) {
    if (tc.name !== 'query_features') continue
    const r = tc.result as QueryResult | null

    for (const f of r?.features ?? []) {
      if (f.geometry) features.push(f)
    }
  }

  // Closures reset per render pass.
  let inBody = false
  let bodyRowIdx = 0

  return (
    <div className="chatbot-message space-y-2 leading-relaxed" style={{ fontSize: '1em' }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Text-level formatting — tighter vertical rhythm so multi-paragraph
          // answers read as a conversation, not a wall of text.
          p: ({ children }) => <p className="my-0 leading-relaxed">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          code: ({ children }) => (
            <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] text-gray-800 dark:bg-gray-700 dark:text-gray-100">
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre className="my-2 overflow-x-auto rounded-md bg-gray-100 p-2 font-mono text-[0.72em] text-gray-800 dark:bg-gray-800 dark:text-gray-100">
              {children}
            </pre>
          ),
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {children}
            </a>
          ),

          // Headings — scaled down since chat bubbles are narrow
          h1: ({ children }) => <h3 className="mt-3 mb-1 text-[1em] font-semibold">{children}</h3>,
          h2: ({ children }) => <h4 className="mt-3 mb-1 text-[0.85em] font-semibold">{children}</h4>,
          h3: ({ children }) => <h5 className="mt-2 mb-1 text-[0.85em] font-semibold">{children}</h5>,

          // Lists
          ul: ({ children }) => <ul className="my-1 list-disc space-y-0.5 pl-5">{children}</ul>,
          ol: ({ children }) => <ol className="my-1 list-decimal space-y-0.5 pl-5">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,

          // Blockquote for context/asides
          blockquote: ({ children }) => (
            <blockquote className="my-2 border-l-2 border-gray-300 pl-3 italic text-gray-600 dark:border-gray-600 dark:text-gray-300">
              {children}
            </blockquote>
          ),

          // Horizontal rule
          hr: () => <hr className="my-3 border-gray-200 dark:border-gray-700" />,

          // Table rendering — bordered, striped-ish, clickable body rows when geometry available
          table: ({ children }) => (
            <div className="my-2 overflow-auto rounded-md border border-gray-200 dark:border-gray-700">
              <table className="w-full text-[0.72em]">{children}</table>
            </div>
          ),
          thead: ({ children }) => {
            inBody = false

            return <thead className="bg-gray-50 dark:bg-gray-800">{children}</thead>
          },
          tbody: ({ children }) => {
            inBody = true
            bodyRowIdx = 0

            return <tbody>{children}</tbody>
          },
          tr: ({ children }) => {
            if (!inBody) return <tr>{children}</tr>
            const idx = bodyRowIdx++
            const feature = features[idx]

            if (!feature?.geometry || !bridge) {
              return <tr className="border-t border-gray-100 dark:border-gray-800">{children}</tr>
            }

            const geometry = feature.geometry

            return (
              <tr
                onClick={() => bridge.emit('zoom-to-feature', { geometry })}
                className="cursor-pointer border-t border-gray-100 hover:bg-blue-50 dark:border-gray-800 dark:hover:bg-blue-900/30"
              >
                {children}
              </tr>
            )
          },
          th: ({ children }) => (
            <th className="px-[0.5em] py-[0.25em] text-left font-medium text-gray-600 dark:text-gray-300">
              {children}
            </th>
          ),
          td: ({ children }) => <td className="px-[0.5em] py-[0.25em] text-gray-700 dark:text-gray-200">{children}</td>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
